import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoAddOutline,
  IoCreateOutline,
  IoSettingsOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  createField,
  deleteField,
  listFields,
  updateField,
} from "../../../api/fieldsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import { stripRichText } from "../../../utils/richText";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import ArticleFormModal from "./ArticleFormModal";
import {
  ARTICLE_FIELD_DEFS,
  articleGroupToForm,
  getArticleTitle,
  groupArticleFields,
  groupFilterFields,
  syncArticleCount,
} from "./articleFieldUtils";
import { useArticlesSection } from "./useArticlesSection";
import FieldInputRenderer from "../fields/FieldInputRenderer";

export default function ArticlesManager() {
  const notify = useNotify();
  const {
    sectionId,
    pageId,
    pageName,
    loading: sectionLoading,
    error: sectionError,
    reload: reloadSection,
  } = useArticlesSection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [sortLabelValue, setSortLabelValue] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const loadFields = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listFields(sectionId);
      const list = Array.isArray(data) ? data : [];
      const sorted = list.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
      setFields(sorted);

      const nextFilters = {};
      sorted.forEach((field) => {
        if (/^filter_\d+$/.test(field.key || "")) {
          nextFilters[field.id] = field.value ?? "";
        }
      });
      setFilterValues(nextFilters);

      const sortField = sorted.find((f) => f.key === "sortLabel");
      setSortLabelValue(sortField?.value ?? "");
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId) loadFields();
  }, [sectionId, loadFields]);

  const articles = useMemo(() => groupArticleFields(fields), [fields]);
  const filters = useMemo(() => groupFilterFields(fields), [fields]);
  const sortLabelField = useMemo(
    () => fields.find((f) => f.key === "sortLabel"),
    [fields]
  );

  const hasSettingsChanges = useMemo(() => {
    if (sortLabelField && sortLabelValue !== (sortLabelField.value ?? "")) {
      return true;
    }
    return filters.some(
      (field) => (filterValues[field.id] ?? "") !== (field.value ?? "")
    );
  }, [sortLabelField, sortLabelValue, filters, filterValues]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((group) => {
      const form = articleGroupToForm(group);
      return (
        stripRichText(form.title).toLowerCase().includes(q) ||
        form.slug.toLowerCase().includes(q) ||
        form.category.toLowerCase().includes(q) ||
        stripRichText(form.author).toLowerCase().includes(q)
      );
    });
  }, [articles, query]);

  async function saveFieldRecord(field, value) {
    await updateField(field.id, {
      label: field.label,
      fieldType: field.fieldType,
      key: field.key,
      isRichText: field.isRichText,
      isRequired: field.isRequired,
      isPublished: field.isPublished,
      sortOrder: field.sortOrder,
      value: value ?? "",
    });
  }

  async function saveArticleField(articleGroup, suffix, value) {
    const def = ARTICLE_FIELD_DEFS.find((d) => d.suffix === suffix);
    if (!def) return;

    const field = articleGroup.fields[suffix];
    const nextValue = value ?? "";

    if (field) {
      const currentValue = field.value ?? "";
      if (nextValue === currentValue) return;
      await saveFieldRecord(field, nextValue);
      return;
    }

    if (!nextValue) return;

    const baseOrder = 20 + articleGroup.index * 10;
    const defIndex = ARTICLE_FIELD_DEFS.findIndex((d) => d.suffix === suffix);

    await createField(sectionId, {
      key: `article_${articleGroup.index}_${suffix}`,
      label: `Article ${articleGroup.index} ${def.label}`,
      fieldType: def.fieldType,
      isRequired: false,
      sortOrder: baseOrder + (defIndex >= 0 ? defIndex : 0),
      value: nextValue,
    });
  }

  async function handleSaveSingleField(suffix, value) {
    if (!sectionId || editingGroup == null) return;

    const freshGroup =
      articles.find((a) => a.index === editingGroup.index) ?? editingGroup;

    await saveArticleField(freshGroup, suffix, value);
    await loadFields();
  }

  async function handleSaveArticle(form) {
    if (!sectionId) return;
    setBusy(true);
    try {
      if (editingGroup) {
        const freshGroup =
          articles.find((a) => a.index === editingGroup.index) ?? editingGroup;
        const originalForm = articleGroupToForm(freshGroup);
        let savedAny = false;

        for (const def of ARTICLE_FIELD_DEFS) {
          const newValue = form[def.suffix] ?? "";
          const oldValue = originalForm[def.suffix] ?? "";
          if (newValue === oldValue) continue;

          await saveArticleField(freshGroup, def.suffix, newValue);
          savedAny = true;
        }

        if (savedAny) {
          notify.success("Article updated");
        } else {
          notify.success("No changes to save");
        }
      } else {
        const indices = articles.map((a) => a.index);
        const nextIndex = indices.length ? Math.max(...indices) + 1 : 0;
        const baseOrder = 20 + nextIndex * 10;

        for (let j = 0; j < ARTICLE_FIELD_DEFS.length; j++) {
          const def = ARTICLE_FIELD_DEFS[j];
          await createField(sectionId, {
            key: `article_${nextIndex}_${def.suffix}`,
            label: `Article ${nextIndex} ${def.label}`,
            fieldType: def.fieldType,
            isRequired: false,
            sortOrder: baseOrder + j,
            value: form[def.suffix] ?? "",
          });
        }

        await syncArticleCount(sectionId, fields, [
          ...articles,
          { index: nextIndex, fields: {} },
        ]);
        notify.success("Article created");
      }

      setModalOpen(false);
      setEditingGroup(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !sectionId) return;
    setBusy(true);
    try {
      const ids = Object.values(deleteTarget.fields).map((f) => f.id);
      for (const id of ids) {
        await deleteField(id);
      }

      const remaining = articles.filter(
        (a) => a.index !== deleteTarget.index
      );
      await syncArticleCount(sectionId, fields, remaining);
      notify.success("Article deleted");
      setDeleteTarget(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveAllSettings() {
    if (!hasSettingsChanges) return;
    setSavingSettings(true);
    try {
      if (
        sortLabelField &&
        sortLabelValue !== (sortLabelField.value ?? "")
      ) {
        await saveFieldRecord(sortLabelField, sortLabelValue);
      }
      for (const field of filters) {
        const next = filterValues[field.id] ?? "";
        if (next !== (field.value ?? "")) {
          await saveFieldRecord(field, next);
        }
      }
      notify.success("Changes saved");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setSavingSettings(false);
    }
  }

  async function addFilter() {
    if (!sectionId) return;
    setBusy(true);
    try {
      const nextIndex = filters.length
        ? Math.max(...filters.map((f) => Number(f.key.split("_")[1]))) + 1
        : 0;

      await createField(sectionId, {
        key: `filter_${nextIndex}`,
        label: `Filter ${nextIndex}`,
        fieldType: "text",
        isRequired: false,
        sortOrder: 1 + nextIndex,
        value: "",
      });

      notify.success("Filter added");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function removeFilter(field) {
    setBusy(true);
    try {
      await deleteField(field.id);
      notify.success("Filter removed");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sectionLoading) return <LoadingState message="Loading articles setup…" />;

  if (sectionError) {
    return (
      <div className="space-y-4">
        <ErrorState message={sectionError} onRetry={reloadSection} />
        {pageId ? (
          <Link
            to={`/cmsadmin/dashboard/pages/${pageId}/sections`}
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to {pageName} sections
          </Link>
        ) : (
          <Link
            to="/cmsadmin/dashboard/pages"
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to Pages
          </Link>
        )}
      </div>
    );
  }

  if (loading) return <LoadingState message="Loading articles…" />;
  if (error) return <ErrorState message={error} onRetry={loadFields} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Articles</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage insight articles for the {pageName} page.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-gray-50"
          >
            <IoSettingsOutline className="h-5 w-5" />
            {settingsOpen ? "Hide settings" : "Page settings"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingGroup(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <IoAddOutline className="h-5 w-5" />
            Add article
          </button>
        </div>
      </div>

      {settingsOpen ? (
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">
            Insights page settings
          </h3>

          {sortLabelField ? (
            <div className="max-w-md">
              <FieldInputRenderer
                field={sortLabelField}
                value={sortLabelValue}
                onChange={setSortLabelValue}
              />
            </div>
          ) : null}

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-700">
                Category filters
              </p>
              <button
                type="button"
                onClick={addFilter}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <IoAddOutline className="h-4 w-4" />
                Add filter
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filters.map((field) => (
                <div
                  key={field.id}
                  className="rounded-xl border border-gray-100 bg-slate-50 p-3"
                >
                  <FieldInputRenderer
                    field={field}
                    value={filterValues[field.id] ?? ""}
                    onChange={(next) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [field.id]: next,
                      }))
                    }
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeFilter(field)}
                      disabled={busy}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={saveAllSettings}
              disabled={savingSettings || !hasSettingsChanges}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {savingSettings ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : null}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by title, slug, category, or author…"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />

      {filtered.length === 0 ? (
        <EmptyState
          title={query ? "No matching articles" : "No articles yet"}
          description={
            query
              ? "Try a different search term."
              : "Create your first article to show on the Insights page."
          }
          action={
            !query ? (
              <button
                type="button"
                onClick={() => {
                  setEditingGroup(null);
                  setModalOpen(true);
                }}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
              >
                Add article
              </button>
            ) : null
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Article
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Category
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 lg:table-cell">
                  Author
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 sm:table-cell">
                  Date
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((group) => {
                const form = articleGroupToForm(group);
                const imageSrc = resolveMediaUrl(form.image);

                return (
                  <tr key={group.index} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            className="h-12 w-16 shrink-0 rounded-lg border border-gray-200 object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-slate-50 text-xs text-slate-400">
                            No img
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">
                            {form.title || getArticleTitle(group)}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            /{form.slug || `article-${group.index}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                      {form.category || "—"}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">
                      {form.author || "—"}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 sm:table-cell">
                      {form.date || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroup(group);
                            setModalOpen(true);
                          }}
                          className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                          aria-label={`Edit ${form.title}`}
                        >
                          <IoCreateOutline className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(group)}
                          className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                          aria-label={`Delete ${form.title}`}
                        >
                          <IoTrashOutline className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ArticleFormModal
        open={modalOpen}
        sectionId={sectionId}
        initialForm={editingGroup ? articleGroupToForm(editingGroup) : null}
        editingArticleKey={editingGroup?.index ?? null}
        isEdit={Boolean(editingGroup)}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSaveArticle}
        onSaveField={editingGroup ? handleSaveSingleField : undefined}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete article?"
        message={
          deleteTarget
            ? `This will permanently delete "${getArticleTitle(deleteTarget)}".`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
